import { useRef, useState } from 'react';
import { Braces, Loader2, Pencil, Plus, Share2, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useCreateNoteTemplate,
  useDeleteNoteTemplate,
  useNoteTemplates,
  useUpdateNoteTemplate,
} from '@/api/hooks';
import { TEMPLATE_VARIABLES } from '@/lib/note-templates';
import { cn } from '@/lib/utils';
import type { NoteTemplate } from '@/api/client';

type Editing = { mode: 'new' } | { mode: 'edit'; template: NoteTemplate } | null;

/**
 * Create / edit / delete note templates. Rendered both inside the picker's dialog
 * (Ticket → Add note → Templates → Manage) and on the Settings page, so it carries no
 * dialog chrome of its own.
 */
export function NoteTemplateManager({ className }: { className?: string }) {
  const templates = useNoteTemplates();
  const remove = useDeleteNoteTemplate();
  const [editing, setEditing] = useState<Editing>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const items = templates.data?.items ?? [];
  const mine = items.filter((t) => t.isOwner);
  const shared = items.filter((t) => !t.isOwner);

  if (editing) {
    return (
      <TemplateEditor
        className={className}
        template={editing.mode === 'edit' ? editing.template : null}
        onDone={() => setEditing(null)}
      />
    );
  }

  // Two-step delete: the first click arms the row, the second actually removes it.
  const handleDelete = (t: NoteTemplate) => {
    if (confirmDelete !== t.id) {
      setConfirmDelete(t.id);
      return;
    }
    remove.mutate(t.id, {
      onSuccess: () => { toast.success(`Deleted "${t.title}"`); setConfirmDelete(null); },
      onError: (err) => toast.error('Failed to delete template', {
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-muted-foreground">
          Reusable note bodies you can drop into any ticket. Yours are private unless you share them.
        </p>
        <Button size="sm" onClick={() => setEditing({ mode: 'new' })}>
          <Plus className="h-3.5 w-3.5"/> New
        </Button>
      </div>

      {templates.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin"/> Loading templates…
        </div>
      ) : templates.error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Could not load templates: {templates.error instanceof Error ? templates.error.message : 'Unknown error'}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No templates yet. Create one to reuse it on any ticket.
        </div>
      ) : (
        <div className="space-y-5">
          <Section title="My templates" count={mine.length}>
            {mine.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onEdit={() => setEditing({ mode: 'edit', template: t })}
                onDelete={() => handleDelete(t)}
                deleting={remove.isPending && confirmDelete === t.id}
                confirming={confirmDelete === t.id}
              />
            ))}
            {mine.length === 0 && (
              <p className="px-1 text-[13px] text-muted-foreground">You have not created any yet.</p>
            )}
          </Section>

          {shared.length > 0 && (
            <Section title="Shared by the team" count={shared.length}>
              {shared.map((t) => <TemplateRow key={t.id} template={t}/>)}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title} <span className="tabular-nums opacity-70">({count})</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TemplateRow({
  template,
  onEdit,
  onDelete,
  deleting,
  confirming,
}: {
  template: NoteTemplate;
  onEdit?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
  confirming?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium text-foreground">{template.title}</span>
            {template.isShared && (
              <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10.5px] text-muted-foreground">
                <Share2 className="h-2.5 w-2.5"/> Shared
              </span>
            )}
            {template.isInternal && (
              <span className="inline-flex items-center rounded bg-amber-500/15 px-1.5 py-0.5 text-[10.5px] text-amber-600 dark:text-amber-400">
                Internal
              </span>
            )}
            {!template.isOwner && template.ownerName && (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
                <Users className="h-2.5 w-2.5"/> {template.ownerName}
              </span>
            )}
          </div>
          <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-[12.5px] text-muted-foreground">
            {template.body}
          </p>
        </div>

        {template.isOwner && (
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} title="Edit">
              <Pencil className="h-3.5 w-3.5"/>
            </Button>
            <Button
              variant={confirming ? 'destructive' : 'ghost'}
              size={confirming ? 'sm' : 'icon'}
              className={confirming ? 'h-7' : 'h-7 w-7'}
              onClick={onDelete}
              disabled={deleting}
              title={confirming ? 'Click again to delete' : 'Delete'}
            >
              {deleting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
                : confirming ? 'Confirm' : <Trash2 className="h-3.5 w-3.5"/>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateEditor({
  template,
  onDone,
  className,
}: {
  template: NoteTemplate | null;
  onDone: () => void;
  className?: string;
}) {
  const create = useCreateNoteTemplate();
  const update = useUpdateNoteTemplate();
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [title, setTitle] = useState(template?.title ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isShared, setIsShared] = useState(template?.isShared ?? false);
  const [isInternal, setIsInternal] = useState(template?.isInternal ?? false);

  const pending = create.isPending || update.isPending;
  const canSave = title.trim().length > 0 && body.trim().length > 0 && !pending;

  /** Drop a {{token}} in at the caret (or over the selection). */
  const insertVariable = (token: string) => {
    const el = bodyRef.current;
    const snippet = `{{${token}}}`;
    if (!el) {
      setBody((prev) => prev + snippet);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? start;
    setBody(body.slice(0, start) + snippet + body.slice(end));
    // Restore focus with the caret just after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    const payload = { title: title.trim(), body: body.trim(), isShared, isInternal };
    const opts = {
      onSuccess: () => { toast.success(template ? 'Template updated' : 'Template created'); onDone(); },
      onError: (err: unknown) => toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
    };
    if (template) update.mutate({ id: template.id, body: payload }, opts);
    else create.mutate(payload, opts);
  };

  return (
    <form onSubmit={submit} className={cn('space-y-4', className)}>
      <div className="space-y-1.5">
        <label className="text-[12px] font-medium text-foreground">Name</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Awaiting customer response"
          maxLength={120}
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[12px] font-medium text-foreground">Note body</label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <Braces className="h-3.5 w-3.5"/> Insert variable
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-72 w-64 overflow-y-auto">
              {TEMPLATE_VARIABLES.map((v) => (
                <DropdownMenuItem key={v.token} onSelect={() => insertVariable(v.token)}>
                  <div className="min-w-0">
                    <div className="text-[12.5px] text-foreground">{v.label}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {`{{${v.token}}}`} &rarr; {v.hint}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={9}
          placeholder={'Hi {{contact.first_name|there}},\n\nThanks for getting in touch...'}
          maxLength={8000}
        />
        <p className="text-[11px] text-muted-foreground">
          Variables are filled in when the template is inserted. Add a fallback with{' '}
          <code className="rounded bg-muted px-1">{'{{contact.first_name|there}}'}</code> for tickets
          where the value is missing.
        </p>
      </div>

      <div className="space-y-2">
        <label className="flex cursor-pointer select-none items-start gap-2 text-[12.5px] text-foreground">
          <input type="checkbox" className="mt-0.5 rounded" checked={isShared} onChange={(e) => setIsShared(e.target.checked)}/>
          <span>
            Share with everyone
            <span className="block text-[11px] text-muted-foreground">
              Other engineers can insert it; only you can edit or delete it.
            </span>
          </span>
        </label>
        <label className="flex cursor-pointer select-none items-start gap-2 text-[12.5px] text-foreground">
          <input type="checkbox" className="mt-0.5 rounded" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)}/>
          <span>
            Internal by default
            <span className="block text-[11px] text-muted-foreground">
              Ticks "Internal" on the note form when this template is inserted.
            </span>
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onDone} disabled={pending}>Cancel</Button>
        <Button type="submit" size="sm" disabled={!canSave}>
          {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : null}
          {template ? 'Save changes' : 'Create template'}
        </Button>
      </div>
    </form>
  );
}
